import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  Barcode,
  Calculator,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  Crop,
  FileQuestion,
  FileText,
  Files,
  FolderTree,
  Gauge,
  GraduationCap,
  Hash,
  Keyboard,
  LoaderCircle,
  Mail,
  Monitor,
  MousePointerClick,
  Package,
  Pause,
  Receipt,
  ScanText,
  Search,
  SeparatorHorizontal,
  Send,
  Shapes,
  Share2,
  ShieldCheck,
  Sparkles,
  Table2,
  Timer,
  Undo2,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useAiReviewAvailable } from './Autopilot';
import { useEngagement } from './EngagementPanels';
import { hostHas } from '../shared/host-capabilities';
import { MoreTipsAsk } from './MoreTipsAsk';
import { tipTelemetry } from './hint-telemetry';
import { SetupStepArt, SetupTipArt, type TipArt } from './SetupJourneyArt';
import {
  overallBar,
  type JourneyModel,
  type JourneyTile,
  type JourneyType,
} from './setup-journey-model';
import {
  advanceTipClock,
  leadTipClock,
  LEARNING_DONE_TIP,
  saveTipClock,
  stepTipClock,
  tipClock,
  tipsFor,
} from './setup-tips';
import './type-tints.css';
import './setup-journey.css';

/**
 * The new job's setup screen (owner-approved October 3, 2026): the page being worked on
 * at the left, exactly as before, and at the right a picture of the step with its own
 * count, the results so far as tiles, and the tips. Presentation only: the caller draws
 * every number from the one progress state (setup-journey-model.ts).
 */

export type JourneyFind = {
  kind: 'title' | 'reference' | 'date' | 'total' | 'table' | 'barcode';
  label: string;
  value: string;
};

const findIcons: Record<JourneyFind['kind'], LucideIcon> = {
  title: FileText,
  reference: Hash,
  date: CalendarDays,
  total: Receipt,
  table: Table2,
  barcode: Barcode,
};

const tileIcons: Record<JourneyTile['id'], LucideIcon> = {
  pages: FileText,
  documents: Files,
  types: Shapes,
  fields: ScanText,
  values: Check,
};

const tipIcons: Record<TipArt, LucideIcon> = {
  review: ClipboardCheck,
  clear: CheckCheck,
  new: FileQuestion,
  learn: GraduationCap,
  keys: Keyboard,
  separator: SeparatorHorizontal,
  pause: Pause,
  aiq: Sparkles,
  plain: ScanText,
  private: ShieldCheck,
  speed: Zap,
  gauge: Gauge,
  autopilot: Send,
  export: Share2,
  lookup: Table2,
  carry: Package,
  math: Calculator,
  assign: MousePointerClick,
  folders: FolderTree,
  windows: Monitor,
  barcode: Barcode,
  focus: Crop,
  search: Search,
  undo: Undo2,
};

const TypeDots = ({ types }: { types: readonly JourneyType[] }) => (
  <span className="sj-dots" aria-hidden="true">
    {types.slice(0, 8).map((type) => (
      <i key={type.name} data-type-shade={type.shade} />
    ))}
  </span>
);

/** Initial learning, then read every file: the same two-part picture the setup choice drew
 * (owner, October 4, 2026: "Initial learning" everywhere, never just "Learn"). */
function JourneyBar({ bar, total }: { bar: JourneyModel['bar']; total: number | null }) {
  const files =
    total && total > 0
      ? `${total.toLocaleString()} ${total === 1 ? 'file' : 'files'}`
      : 'your files';
  const reading = bar.learnDone && !bar.readDone;
  return (
    <div className="sj-bar" role="group" aria-label={`Initial learning, then read all ${files}`}>
      <div className={`sj-bar-part sj-bar-learn${bar.learnDone ? ' sj-bar-done' : ''}`}>
        <span className="sj-bar-label">
          {bar.learnDone && <Check size={13} strokeWidth={3} aria-hidden="true" />}Initial learning
        </span>
        <div
          className="sj-bar-track"
          role="progressbar"
          aria-label="Initial learning"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(bar.learn * 100)}
        >
          <span
            className={!bar.learnDone && bar.learn > 0 ? 'sj-bar-moving' : undefined}
            style={{ width: `${bar.learn * 100}%` }}
          />
        </div>
      </div>
      <div className="sj-bar-part sj-bar-read">
        <span className="sj-bar-label">
          {bar.readDone ? (
            <>
              <Check size={13} strokeWidth={3} aria-hidden="true" />
              All {files} read
            </>
          ) : (
            <>
              Read all {files}
              {reading && bar.readCount !== null && bar.readCount > 0 && (
                <b>· {bar.readCount.toLocaleString()} read</b>
              )}
              {reading && bar.readLeft && (
                <span className="sj-bar-left">· {bar.readLeft.toLocaleLowerCase()}</span>
              )}
            </>
          )}
        </span>
        <div
          className="sj-bar-track"
          role="progressbar"
          aria-label={`Read all ${files}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(bar.read * 100)}
        >
          <span
            className={reading && bar.read > 0 ? 'sj-bar-moving' : undefined}
            style={{ width: `${bar.read * 100}%` }}
          />
          {bar.mark !== null && !bar.readDone && (
            <i className="sj-bar-mark" style={{ left: `${bar.mark * 100}%` }}>
              <em>Start checking</em>
            </i>
          )}
        </div>
      </div>
    </div>
  );
}

function Tiles({ tiles, folding }: { tiles: readonly JourneyTile[]; folding: boolean }) {
  const freshest = [...tiles].reverse().find((tile) => tile.value !== null)?.id;
  return (
    <div className={`sj-tiles${folding ? ' sj-tiles-folding' : ''}`} aria-label="Results so far">
      {tiles.map((tile, index) => {
        const Icon = tileIcons[tile.id];
        return (
          <div
            key={`${tile.id}:${tile.value === null ? 'ghost' : 'done'}`}
            style={
              folding ? { animationDelay: `${(tiles.length - 1 - index) * 0.09}s` } : undefined
            }
            className={`sj-tile${tile.value === null ? ' sj-tile-ghost' : ''}${tile.id === freshest && !folding ? ' sj-tile-fresh' : ''}`}
          >
            <span className="sj-tile-top">
              {tile.value !== null && <b>{tile.value.toLocaleString()}</b>}
              <Icon size={18} aria-hidden="true" />
            </span>
            <small>{tile.label}</small>
          </div>
        );
      })}
    </div>
  );
}

function TypeChips({
  types,
  activeType,
  tentative,
}: {
  types: readonly JourneyType[];
  activeType?: string | null;
  tentative: boolean;
}) {
  return (
    <ul
      className="sj-types"
      aria-label={tentative ? 'Document types found so far' : 'Document types'}
    >
      {/* While setup learns, two names can still become one when it saves (6 shown, then 5 saved:
        owner, October 10, 2026), so the list says it is not final yet. */}
      {tentative && (
        <li className="sj-types-note" aria-hidden="true">
          Found so far
        </li>
      )}
      {types.map((type) => (
        <li
          key={type.name}
          data-type-shade={type.shade}
          className={`sj-type sj-type-${type.state}${type.name === activeType ? ' sj-type-active' : ''}`}
        >
          {type.state === 'working' ? (
            <LoaderCircle className="spin" size={13} aria-hidden="true" />
          ) : (
            <i aria-hidden="true" />
          )}
          {type.name}
          {type.state === 'ready' && type.fields.length > 0 && <small>{type.fields.length}</small>}
        </li>
      ))}
    </ul>
  );
}

function LearnedDeck({ types }: { types: readonly JourneyType[] }) {
  const fields = types.reduce((sum, type) => sum + type.fields.length, 0);
  return (
    <div className="sj-deck">
      {types.map((type) => (
        <article key={type.name} className="sj-card" data-type-shade={type.shade}>
          <div className="sj-card-shade" aria-hidden="true">
            <span className="sj-card-page sj-card-back">
              <i />
              <i />
              <i />
            </span>
            <span className="sj-card-page sj-card-front">
              <i />
              <i />
              <i />
              <i />
            </span>
          </div>
          <div className="sj-card-body">
            <h4 title={type.name}>{type.name}</h4>
            <p className="sj-card-count">
              <b>{type.fields.length}</b>
              <small>{type.fields.length === 1 ? 'field' : 'fields'}</small>
            </p>
            <p className="sj-card-fields">
              {type.fields.slice(0, 2).map((field) => (
                <span key={field}>{field}</span>
              ))}
              {type.fields.length > 2 && (
                <span className="sj-card-more">+{type.fields.length - 2}</span>
              )}
            </p>
          </div>
        </article>
      ))}
      <div className="sj-total">
        <div>
          <b>{types.length}</b>
          <small>{types.length === 1 ? 'document type' : 'document types'}</small>
        </div>
        <div>
          <b>{fields}</b>
          <small>fields</small>
        </div>
      </div>
    </div>
  );
}

/** The tips at the bottom, and the one that is open. Their clock survives a remount (setup-tips.ts). */
export function SetupTips({
  jobId,
  learningDone = false,
}: {
  jobId: string;
  learningDone?: boolean;
}) {
  const aiReview = useAiReviewAvailable();
  const tips = useMemo(() => tipsFor({ aiReview }), [aiReview]);
  const ids = useMemo(() => tips.map((tip) => tip.id), [tips]);
  const [clock, setClock] = useState(() => tipClock(jobId, Date.now()));
  const held = useRef(false);
  // "Get more tips like this": offered until this computer has opted in (MoreTipsAsk.tsx). The
  // tips hold still while its pop-up is open, wherever the pointer is.
  const engagement = useEngagement();
  const [asking, setAsking] = useState(false);
  const askingRef = useRef(false);
  const offerTips =
    // PAIGE: a host without a tips email hides it (`tips-email`); Paige keeps the opt-in per person
    // (paige/server-engagement.ts).
    hostHas('tips-email') &&
    Boolean(window.capturePointDesktop?.setEngagementConsent) &&
    engagement !== null &&
    engagement.consent.state !== 'opted-in';
  useEffect(() => {
    const timer = window.setInterval(
      () =>
        setClock((current) => {
          const next = advanceTipClock(current, Date.now(), ids, held.current || askingRef.current);
          if (next !== current) saveTipClock(jobId, next);
          return next;
        }),
      500,
    );
    return () => window.clearInterval(timer);
  }, [jobId, ids]);
  // Learning has ended: the next tip says it keeps learning as you check.
  useEffect(() => {
    if (!learningDone) return;
    setClock((current) => {
      const next = leadTipClock(current, Date.now(), ids, LEARNING_DONE_TIP);
      if (next !== current) saveTipClock(jobId, next);
      return next;
    });
  }, [learningDone, jobId, ids]);
  // Each tip seen, closed or opened, is reported once per session; Learn more reports it opened.
  const showing = tips.length ? (clock.openId ?? tips[clock.index % tips.length].id) : null;
  useEffect(() => {
    if (showing) tipTelemetry.shown(showing, jobId);
  }, [showing, jobId]);
  if (!tips.length) return null;
  const tip = tips[clock.index % tips.length];
  const open = clock.openId ? (tips.find((item) => item.id === clock.openId) ?? null) : null;
  const update = (next: typeof clock) => {
    saveTipClock(jobId, next);
    setClock(next);
  };
  const close = () => {
    held.current = false;
    update({ ...clock, openId: null, shownAt: Date.now() });
  };
  const hold = {
    onMouseEnter: () => {
      held.current = true;
    },
    onMouseLeave: () => {
      held.current = false;
    },
    onFocus: () => {
      held.current = true;
    },
    onBlur: () => {
      held.current = false;
    },
  };
  const Icon = tipIcons[tip.art];
  if (open) {
    const OpenIcon = tipIcons[open.art];
    return (
      <section className="sj-tip-open" role="dialog" aria-label={open.headline} {...hold}>
        <header>
          <span className={`sj-tip-icon sj-tip-${open.kind}`}>
            <OpenIcon size={20} aria-hidden="true" />
          </span>
          <h3 key={open.id}>{open.headline}</h3>
          <button
            type="button"
            data-telemetry="tip-close"
            className="sj-tip-close"
            aria-label="Close tip"
            onClick={close}
          >
            <X size={18} />
          </button>
        </header>
        {/* Words at the left, the picture beside them, back and next right under them: short
          enough to sit in the space above the tip instead of covering the screen (owner, October
          3, 2026). The X closes it; there is no Got it (owner, October 4, 2026). */}
        <div key={open.id} className="sj-tip-page">
          <p>{open.description}</p>
          <div className="sj-tip-art">
            <SetupTipArt art={open.art} />
          </div>
          {open.chips && (
            <ul className="sj-tip-chips">
              {open.chips.map((chip) => (
                <li key={chip}>{chip}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="sj-tip-actions">
          <span className="sj-tip-steps">
            <button
              type="button"
              className="sj-tip-step"
              data-telemetry="tip-previous"
              aria-label="Previous tip"
              onClick={() => update(stepTipClock(clock, Date.now(), ids, -1))}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="sj-tip-step"
              data-telemetry="tip-next"
              aria-label="Next tip"
              onClick={() => update(stepTipClock(clock, Date.now(), ids, 1))}
            >
              <ChevronRight size={18} />
            </button>
          </span>
          {offerTips && (
            <button
              type="button"
              className="sj-tip-email"
              data-telemetry="tip-more-tips"
              onClick={() => {
                askingRef.current = true;
                setAsking(true);
              }}
            >
              <Mail size={14} aria-hidden="true" />
              Get more tips like this
            </button>
          )}
        </div>
        {asking && (
          <MoreTipsAsk
            onClose={() => {
              askingRef.current = false;
              setAsking(false);
            }}
          />
        )}
        {/* How long until the next tip opens on its own; still while held. */}
        <span key={`${open.id}:${clock.shownAt}`} className="sj-tip-timer" aria-hidden="true" />
      </section>
    );
  }
  return (
    <aside className="sj-tips" aria-label="Tips" {...hold}>
      <div key={tip.id} className="sj-tip">
        <span className={`sj-tip-icon sj-tip-${tip.kind}`}>
          <Icon size={20} aria-hidden="true" />
        </span>
        <span className="sj-tip-text">
          <strong>{tip.headline}</strong>
          <span>{tip.line}</span>
        </span>
        {/* One quiet "Learn more" that opens the tip in place (owner, October 3, 2026: "some sort
          of expand or learn more" rather than a "Check out …" button per tip). */}
        <button
          type="button"
          className="sj-tip-more"
          aria-expanded={false}
          data-telemetry="tip-learn-more"
          onClick={() => {
            tipTelemetry.opened(tip.id, jobId);
            update({ ...clock, openId: tip.id, shownAt: Date.now() });
          }}
        >
          Learn more
          <ChevronUp size={15} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}

export function SetupJourney({
  model,
  total,
  types,
  activeType,
  finds,
  learned,
  fold,
  preview,
  previewLabel,
  pageNumber,
  actions,
  tips,
  onStartChecking,
  note,
}: {
  model: JourneyModel;
  /** Files in the whole selection, for the two-part bar. */
  total: number | null;
  types: readonly JourneyType[];
  activeType?: string | null;
  finds: readonly JourneyFind[];
  /** What setup learned, once it has (the saved document types and their fields). */
  learned: readonly JourneyType[] | null;
  /** The results to fold into "Learned enough to start" when this screen opens right after learning. */
  fold: readonly JourneyTile[] | null;
  preview: ReactNode;
  previewLabel: string;
  pageNumber?: number;
  actions?: ReactNode;
  tips?: ReactNode;
  onStartChecking?: () => void;
  /** One short honest line under the step, when the computer explains a slow part. */
  note?: string | null;
}) {
  const [folding, setFolding] = useState(Boolean(fold?.some((tile) => tile.value !== null)));
  useEffect(() => {
    if (!folding) return;
    const timer = window.setTimeout(() => setFolding(false), 2_600);
    return () => window.clearTimeout(timer);
  }, [folding]);
  const [deckOpen, setDeckOpen] = useState(false);
  const learning = model.phase === 'learning' || model.phase === 'saving';
  const ready = model.phase === 'ready';
  const showTypes = learning && model.part >= 2 && types.length > 0;
  const showFinds = learning && model.part === 0 && finds.length > 0;

  // The state as data too, for the release gate that proves it only moves forward.
  const barValue = overallBar(model.bar);
  return (
    <section
      className="setup-journey"
      data-phase={model.phase}
      data-part={model.part}
      data-bar={(barValue * 100).toFixed(2)}
      data-count={model.count?.value}
      data-total={model.count?.total}
      aria-label="Setting up this job"
    >
      <figure className="sj-paper">
        <div className="processing-workspace-preview">{preview}</div>
        {previewLabel && (
          <figcaption>
            <span title={previewLabel}>
              <FileText size={16} aria-hidden="true" />
              <span>{previewLabel}</span>
            </span>
            {typeof pageNumber === 'number' && pageNumber > 0 && (
              <strong>Page {pageNumber.toLocaleString()}</strong>
            )}
          </figcaption>
        )}
      </figure>

      <div className="sj-side">
        <header className="sj-top">
          <JourneyBar bar={model.bar} total={total} />
          {/* No running clock (owner, October 14, 2026): time left is the time people use, and a
            total kept true across pauses, a closed app and restarts was plumbing, not help. */}
          <div className="sj-tools">{actions}</div>
        </header>

        <section
          className={`sj-hero${ready ? ' sj-hero-ready' : ''}${deckOpen ? ' sj-hero-compact' : ''}`}
          aria-live="polite"
        >
          {!deckOpen && (
            <div className="sj-art">
              <SetupStepArt art={model.hero.art} />
            </div>
          )}
          <div className="sj-hero-text">
            <h2>{model.hero.step}</h2>
            {model.hero.value !== null && (
              <p className="sj-count">
                <b>{model.hero.value}</b>
                {model.hero.of && <span>{model.hero.of}</span>}
              </p>
            )}
            {ready ? (
              <>
                <button
                  type="button"
                  className="sj-start"
                  data-telemetry="start-checking"
                  onClick={onStartChecking}
                >
                  Start checking
                </button>
                {/* Every file can be read before the last results are saved; it never says "when all
                60 are read" under "All 60 files read" (seen in the real app, October 3, 2026). */}
                {model.bar.readDone ? (
                  <p className="sj-or">Or stay. The job opens by itself when it’s done.</p>
                ) : total ? (
                  <p className="sj-or">
                    Or stay. The job opens when all {total.toLocaleString()} are read.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {model.hero.live && (
                  <p className="sj-live">
                    <i aria-hidden="true" />
                    {model.hero.live}
                  </p>
                )}
                {model.hero.left && (
                  <p className="sj-left">
                    <Timer size={14} aria-hidden="true" />
                    {model.hero.left}
                  </p>
                )}
                {note && <p className="sj-note">{note}</p>}
              </>
            )}
          </div>
        </section>

        {learning && <Tiles tiles={model.tiles} folding={false} />}
        {showFinds && (
          <div className="sj-finds" aria-label="On this page">
            <h3>On this page</h3>
            <ul>
              {finds.map((find, index) => {
                const Icon = findIcons[find.kind];
                return (
                  <li key={`${find.kind}:${index}`} className={`sj-find sj-find-${find.kind}`}>
                    <Icon size={15} aria-hidden="true" />
                    <span>
                      <small>{find.label}</small>
                      <strong title={find.value}>{find.value}</strong>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {showTypes && (
          <TypeChips types={types} activeType={activeType} tentative={model.phase === 'learning'} />
        )}

        {!learning && folding && fold && (
          <div className="sj-fold">
            <Tiles tiles={fold} folding />
          </div>
        )}
        {!learning && !folding && learned && (
          <section className={`sj-learned${fold ? ' sj-learned-arrive' : ''}`} aria-label="Learned">
            <span className="sj-medal">
              <Check size={22} strokeWidth={2.6} aria-hidden="true" />
            </span>
            {/* Learning goes on after setup; the row must not read as "done learning" (owner, October 3, 2026). */}
            <span className="sj-learned-text">
              <strong>Learned enough to start</strong>
              <span className="sj-learned-sub">
                <TypeDots types={learned} />
                <small>Keeps learning from every document you check</small>
              </span>
            </span>
            <button
              type="button"
              className="sj-show"
              aria-expanded={deckOpen}
              onClick={() => setDeckOpen((open) => !open)}
            >
              {deckOpen ? (
                <>
                  Hide
                  <ChevronUp size={15} aria-hidden="true" />
                </>
              ) : (
                <>
                  Show what it learned
                  <ChevronDown size={15} aria-hidden="true" />
                </>
              )}
            </button>
          </section>
        )}
        {!learning && !folding && learned && deckOpen && <LearnedDeck types={learned} />}

        {tips}
      </div>
    </section>
  );
}
